import React from 'react'
import PropTypes from 'prop-types'
import {
  Card, Row,
  Col, Skeleton, Button,
  Popconfirm, Form, Input, DatePicker,
  Icon, Table, Tooltip,
} from 'antd'
import moment from 'moment'
import SimpleReactLightbox, { SRLWrapper } from 'simple-react-lightbox'
import history from 'utils/history'
import Helper from 'utils/Helper'
import { StatusBadge, formatDateTime } from 'components/pages/updateData/List'
import DetailTable from './detailTable'
import MemberCard from './memberCard'

const MONTHS_ID = [
  'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
  'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember',
]

// "2019-10-12" -> "12 Oktober 2019"
const formatDateID = (value) => {
  if (!value) return '-'
  const d = moment(value, 'YYYY-MM-DD')
  if (!d.isValid()) return value
  return `${d.date()} ${MONTHS_ID[d.month()]} ${d.year()}`
}

const formatRupiah = (value) => {
  if (value === undefined || value === null || value === '') return '-'
  return `Rp${Helper.currency(Math.round(Number(value)))}`
}

const formatAge = value => (value ? `${value} Tahun` : '-')

const formatInvestmentOption = (option) => {
  if (!option || !option.name) return '-'
  const hasCombination = option.combination_value_deposit > 0 || option.combination_value_obligation > 0
  return (
    <React.Fragment>
      {option.name}
      {hasCombination && (
        <React.Fragment>
          <br />
          <span className="text-muted" style={{ fontSize: '13px' }}>
            {`Min. ${option.combination_value_deposit}% Deposito, Maks. ${option.combination_value_obligation}% Obligasi`}
          </span>
        </React.Fragment>
      )}
    </React.Fragment>
  )
}

const Field = ({ label, children }) => (
  <Col span={12} className="mb-2">
    <p className="mr-3 mb-2 font-weight-bold">{label}</p>
    <div style={{ fontSize: '16px' }}>{children}</div>
  </Col>
)

Field.propTypes = {
  label: PropTypes.string,
  children: PropTypes.node,
}

const beneficiaryColumns = [
  {
    title: 'No',
    key: 'no',
    width: 60,
    render: (text, record, index) => index + 1,
  },
  { title: 'Nama Penerima', dataIndex: 'name', key: 'name' },
  {
    title: 'Hubungan', dataIndex: 'relationship', key: 'relationship', render: text => text || '-',
  },
  {
    title: 'Tanggal Lahir', dataIndex: 'dob', key: 'dob', render: text => formatDateID(text),
  },
]

// UUID is the request id; show the first block, full value on hover (same as Pengkinian Data list)
const shortId = id => (id ? id.split('-')[0].toUpperCase() : '-')

const profileHistoryColumns = (meta, canOpenDetail) => [
  {
    title: 'No',
    key: 'no',
    width: 60,
    render: (text, record, index) => ((meta.per_page || 10) * ((meta.current_page || 1) - 1)) + index + 1,
  },
  {
    title: 'Request ID',
    dataIndex: 'id',
    key: 'id',
    render: text => (
      <Tooltip title={text}>
        <span style={{ fontFamily: 'monospace' }}>{shortId(text)}</span>
      </Tooltip>
    ),
  },
  {
    title: 'Tanggal Pengajuan', dataIndex: 'submitted_at', key: 'submitted_at', render: text => formatDateTime(text),
  },
  {
    title: 'Tanggal Review', dataIndex: 'reviewed_at', key: 'reviewed_at', render: text => formatDateTime(text),
  },
  {
    title: 'Review Oleh',
    dataIndex: 'reviewed_by',
    key: 'reviewed_by',
    render: reviewer => (reviewer && reviewer.name ? reviewer.name : '-'),
  },
  {
    title: 'Catatan Reviewer',
    dataIndex: 'notes',
    key: 'notes',
    width: 240,
    render: text => (text ? (
      <Tooltip title={text}>
        <div style={{
          maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}
        >
          {text}
        </div>
      </Tooltip>
    ) : '-'),
  },
  {
    title: 'Status', dataIndex: 'status', key: 'status', render: text => <StatusBadge status={text} />,
  },
  ...(canOpenDetail ? [{
    title: 'Aksi',
    key: 'action',
    width: 80,
    render: (text, record) => (
      <div className="d-flex justify-content-around">
        <Icon type="eye" className="cursor-pointer" onClick={() => history.push(`/update-data/${record.id}/detail`)} />
      </div>
    ),
  }] : []),
]

const DetailUserPage = ({
  detailUser, isFetching, handleResendEmail,
  handleResendEmailPin, isSubmit, isSubmitEdit, match,
  currentUser, form,
  editingField, handleStartEdit, handleCancelEdit, handleSubmitField,
  isDownloading, handleDownloadDocument,
  profileHistory, handleProfileHistoryPage,
  certificate,
}) => {
  const lightBoxSetting = {
    thumbnails: {
      showThumbnails: false,
    },
    buttons: {
      showAutoplayButton: false,
      showDownloadButton: false,
      showNextButton: false,
      showPrevButton: false,
      showThumbnailsButton: false,
      showFullscreenButton: false,
    },
  }
  const { getFieldDecorator, validateFields, resetFields } = form
  const canWrite = currentUser.permissions && currentUser.permissions.indexOf('manage-member-management-write') > -1
  const canReadHistory = currentUser.permissions && currentUser.permissions.indexOf('manage-pengkinian-data-read') > -1
  const canOpenHistoryDetail = currentUser.permissions && currentUser.permissions.indexOf('manage-pengkinian-data-write') > -1

  const submitField = (fieldName) => {
    validateFields([fieldName], (err, values) => {
      if (!err) {
        handleSubmitField(values)
      }
    })
  }

  const cancelField = (fieldName) => {
    resetFields([fieldName])
    handleCancelEdit()
  }

  const inlineActions = fieldName => (
    <span className="ml-2" style={{ whiteSpace: 'nowrap' }}>
      <Button size="small" className="mr-2" onClick={() => cancelField(fieldName)} disabled={isSubmitEdit}>Cancel</Button>
      <Button size="small" type="primary" loading={isSubmitEdit} onClick={() => submitField(fieldName)}>Submit</Button>
    </span>
  )

  const editIcon = field => (canWrite && !editingField ? (
    <Icon
      type="edit"
      className="ml-2"
      style={{ color: '#003db2', cursor: 'pointer' }}
      onClick={() => handleStartEdit(field)}
    />
  ) : null)

  // responsive so 3 columns (Sertifikat | KTP | NPWP) fit; caps at 300px like before
  const docImgStyle = {
    width: '100%', maxWidth: 300, height: 'auto', borderRadius: 4, objectFit: 'cover',
  }
  const documentPreview = (src, alt) => (src ? (
    <SimpleReactLightbox>
      <SRLWrapper options={lightBoxSetting}>
        <img src={src} alt={alt} style={{ ...docImgStyle, cursor: 'pointer' }} />
      </SRLWrapper>
    </SimpleReactLightbox>
  ) : (
    <img src="/assets/default.jpg" alt="no img" style={docImgStyle} />
  ))

  return (
    <div className="pb-5">
      <div className="btn-primary-wrapper mb-3">
        <Button type="primary" icon="left" onClick={() => history.goBack()}>Go Back</Button>
      </div>
      {isFetching
        ? [1, 2, 3].map(index => (
          <Card key={index} className="mb-3">
            <div>
              <Skeleton active />
            </div>
          </Card>
        ))
        : (
          <React.Fragment>
            <Card className="btn-primary-wrapper mb-3">
              <div className="d-flex justify-content-between">
                <div>
                  {canWrite && (
                  <Popconfirm
                    title="Are you sure you want to resend the Email?"
                    okText="Resend Email"
                    cancelText="No"
                    placement="topLeft"
                    onConfirm={() => handleResendEmail()}
                  >
                    <Button className="mr-3" loading={isSubmit.resendEmail}>Resend Email</Button>
                  </Popconfirm>
                  )}
                  <Popconfirm
                    title="Are you sure you want to resend the Forgot Pin Email?"
                    okText="Resend Forgot Pin Email"
                    cancelText="No"
                    placement="topLeft"
                    onConfirm={() => handleResendEmailPin()}
                  >
                    <Button loading={isSubmit.resendPin}>Resend Email Forgot Pin</Button>
                  </Popconfirm>
                </div>
                <div className="d-flex">
                  <Button className="mr-2" style={{ borderColor: '#003db2', color: '#003db2' }} onClick={() => history.push(`/user/temanku/${match.params.id}`)}>Temanku</Button>
                  <Button className="mr-2" style={{ borderColor: '#003db2', color: '#003db2' }} onClick={() => history.push(`/user/pakde/${match.params.id}`)}>Downline</Button>
                  <Button type="primary" onClick={() => history.push(`/user/history-point/${match.params.id}`)}>History Point</Button>
                </div>
              </div>
            </Card>

            {/* 1. Customer Detail */}
            <Form onSubmit={e => e.preventDefault()}>
              <Card className="mb-3">
                <Row gutter={[24, 6]}>
                  <Col span={24}>
                    <h5 className="mb-4">Customer Detail</h5>
                  </Col>
                  <Col span={24}>
                    <h6 className="mb-3 text-muted">Informasi Pribadi</h6>
                  </Col>
                  <Field label="Member ID:">{detailUser.registered_number || '-'}</Field>
                  <Field label="Name:">{detailUser.name || '-'}</Field>
                  <Field label="No. KTP:">{detailUser.identity_number || '-'}</Field>
                  <Field label="Place of Birth:">{detailUser.birth_place || '-'}</Field>
                  <Field label="Date of Birth:">
                    {editingField === 'dob'
                      ? (
                        <div className="d-flex align-items-start">
                          <Form.Item className="mb-0">
                            {getFieldDecorator('birthdate', {
                              initialValue: detailUser.dob ? moment(detailUser.dob, 'YYYY-MM-DD') : null,
                              rules: [{ required: true, message: 'Date of birth is required' }],
                            })(
                              <DatePicker
                                format="DD/MM/YYYY"
                                disabledDate={current => current && current > moment().endOf('day')}
                              />,
                            )}
                          </Form.Item>
                          {inlineActions('birthdate')}
                        </div>
                      )
                      : (
                        <span>
                          {formatDateID(detailUser.dob)}
                          {editIcon('dob')}
                        </span>
                      )
                    }
                  </Field>
                  <Field label="Phone Number:">{detailUser.phone_number || '-'}</Field>
                  <Field label="Email:">
                    {editingField === 'email'
                      ? (
                        <div className="d-flex align-items-start">
                          <Form.Item className="mb-0" style={{ minWidth: '320px' }}>
                            {getFieldDecorator('email', {
                              initialValue: detailUser.email || '',
                              rules: [
                                { required: true, message: 'Please enter a valid email address.' },
                                { type: 'email', message: 'Please enter a valid email address.' },
                              ],
                            })(
                              <Input onPressEnter={() => submitField('email')} />,
                            )}
                          </Form.Item>
                          {inlineActions('email')}
                        </div>
                      )
                      : (
                        <span className="font-weight-bold">
                          {detailUser.email || '-'}
                          {editIcon('email')}
                        </span>
                      )
                    }
                  </Field>
                </Row>
                <Row gutter={[24, 6]} className="mt-3">
                  <Col span={24}>
                    <h6 className="mb-3 text-muted">Alamat</h6>
                  </Col>
                  <Field label="KTP Address:">{detailUser.ktp_addr || '-'}</Field>
                  <Field label="Postal Code:">{detailUser.postal_code || '-'}</Field>
                </Row>
              </Card>
            </Form>

            {/* 4. Membership Detail (view only) */}
            <Card className="mb-3">
              <Row gutter={[24, 6]}>
                <Col span={24}>
                  <h5 className="mb-4">Membership Detail</h5>
                </Col>
                <Field label="Tanggal Bergabung:">{formatDateID(detailUser.joined_date)}</Field>
                <Field label="Lama Menabung:">{detailUser.long_time_deposit || '-'}</Field>
                <Field label="Usia Manfaat Pensiun Normal:">{formatAge(detailUser.normal_retirement_age)}</Field>
                <Field label="Usia Manfaat Pensiun Dipercepat:">{formatAge(detailUser.early_retirement_age)}</Field>
                <Field label="Iuran:">{formatRupiah(detailUser.contribution_amount)}</Field>
                <Field label="Frekuensi Iuran:">{detailUser.contribution_frequency || '-'}</Field>
                <Field label="Pilihan Investasi:">{formatInvestmentOption(detailUser.investment_option)}</Field>
                <Field label="Income:">{detailUser.income && detailUser.income.name ? detailUser.income.name : '-'}</Field>
                <Field label="Source Income:">{detailUser.source_of_income || '-'}</Field>
              </Row>
            </Card>

            {/* 5. Left: Dokumen (KTP | NPWP) + Beneficiary Detail stacked; right: Sertifikat Peserta.
                Sertifikat card only when account active (API 404 otherwise); Dokumen widens to full. */}
            {(() => {
              const hasCertificate = certificate.isFetching || !!certificate.data
              return (
                <Row gutter={[16, 16]} className="mb-3" type="flex">
                  <Col xs={24} xl={hasCertificate ? 16 : 24}>
                    <Card>
                      <Row gutter={[24, 6]}>
                        <Col span={24}>
                          <h5 className="mb-4">Dokumen</h5>
                        </Col>
                      </Row>
                      <Row gutter={[24, 24]}>
                        <Col xs={24} md={12} className="pb-3">
                          <p className="mr-3 mb-2 font-weight-bold">KTP Photo:</p>
                          {documentPreview(detailUser.identity_photo, 'KTP Photo')}
                          {detailUser.identity_photo && (
                            <div className="mt-3">
                              <Button icon="download" loading={isDownloading.ktp} onClick={() => handleDownloadDocument('ktp')}>Download KTP</Button>
                            </div>
                          )}
                        </Col>
                        <Col xs={24} md={12} className="pb-3">
                          <p className="mr-3 mb-2 font-weight-bold">NPWP Photo:</p>
                          {documentPreview(detailUser.npwp_photo, 'NPWP Photo')}
                          {detailUser.npwp_photo && (
                            <div className="mt-3">
                              <Button icon="download" loading={isDownloading.npwp} onClick={() => handleDownloadDocument('npwp')}>Download NPWP</Button>
                            </div>
                          )}
                        </Col>
                      </Row>
                    </Card>

                    {/* Beneficiary Detail (view only) — same width as Dokumen */}
                    <Card className="mt-3">
                      <Row gutter={[24, 6]}>
                        <Col span={24}>
                          <h5 className="mb-4">Beneficiary Detail</h5>
                        </Col>
                        <Col span={24}>
                          <Table
                            rowKey={(record, index) => `${record.name}-${index}`}
                            columns={beneficiaryColumns}
                            dataSource={detailUser.beneficiaries || []}
                            pagination={false}
                            size="middle"
                            locale={{ emptyText: 'Belum ada data penerima manfaat' }}
                          />
                        </Col>
                      </Row>
                    </Card>
                  </Col>
                  {hasCertificate && (
                    <Col xs={24} xl={8}>
                      <Card style={{ height: '100%' }}>
                        <Row gutter={[24, 6]}>
                          <Col span={24}>
                            <h5 className="mb-4">Sertifikat Peserta</h5>
                          </Col>
                          <Col span={24}>
                            <MemberCard data={certificate.data} isFetching={certificate.isFetching} />
                          </Col>
                        </Row>
                      </Card>
                    </Col>
                  )}
                </Row>
              )
            })()}
          </React.Fragment>
        )
      }

      {/* 10. Fund Position Report */}
      <Card className="mb-3">
        <DetailTable title="Laporan Posisi Dana 6 Bulan Berjalan" data={detailUser.rolling_transaction_history || []} isFetching={isFetching} />
      </Card>

      <Card className="mb-3">
        <DetailTable title="Laporan Posisi Dana 6 Bulan Terakhir" data={detailUser.semester_transaction_history || []} isFetching={isFetching} />
      </Card>

      {/* 8. Riwayat Pengajuan Perubahan Data (Pengkinian Data) */}
      {canReadHistory && (
        <Card className="mb-3">
          <Row gutter={[24, 6]}>
            <Col span={24}>
              <h5 className="mb-4">Riwayat Pengajuan Perubahan Data</h5>
            </Col>
            <Col span={24}>
              <Table
                rowKey="id"
                columns={profileHistoryColumns(profileHistory.meta, canOpenHistoryDetail)}
                dataSource={profileHistory.data}
                loading={profileHistory.isFetching}
                size="middle"
                scroll={{ x: 'max-content' }}
                locale={{ emptyText: 'Belum ada pengajuan perubahan data' }}
                pagination={{
                  showTotal: (total, range) => `Showing ${range[0]} to ${range[1]} of ${total} entries`,
                  total: profileHistory.meta.total_count || profileHistory.data.length,
                  current: profileHistory.meta.current_page || 1,
                  pageSize: 10,
                  onChange: handleProfileHistoryPage,
                }}
              />
            </Col>
          </Row>
        </Card>
      )}
    </div>
  )
}

DetailUserPage.propTypes = {
  detailUser: PropTypes.object,
  isFetching: PropTypes.bool,
  handleResendEmail: PropTypes.func,
  handleResendEmailPin: PropTypes.func,
  isSubmit: PropTypes.object,
  match: PropTypes.object,
  currentUser: PropTypes.object,
  form: PropTypes.any,
  isSubmitEdit: PropTypes.bool,
  editingField: PropTypes.string,
  handleStartEdit: PropTypes.func,
  handleCancelEdit: PropTypes.func,
  handleSubmitField: PropTypes.func,
  isDownloading: PropTypes.object,
  handleDownloadDocument: PropTypes.func,
  profileHistory: PropTypes.object,
  handleProfileHistoryPage: PropTypes.func,
  certificate: PropTypes.object,
}

export default Form.create({ name: 'userDetailForm' })(DetailUserPage)
